import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { trackEvent } from '@zep/logger/tracking';
import { editingQuizIdAtom } from '../../../components/focus/editModeAtom';
import { useAiBuilderChatAutoScroll } from '../hooks/useAiBuilderChatAutoScroll';
import { useAiBuilderCloseConfirmDialog } from '../hooks/useAiBuilderCloseConfirmDialog';
import { useAiBuilderComposerFileDrop } from '../hooks/useAiBuilderComposerFileDrop';
import { useAiBuilderChatActions, useAiBuilderChatComposerSelector, useAiBuilderChatMessagesSelector, useAiBuilderChatSessionIdSafe } from '../stores';
import { useAiBuilderActiveOperation } from '../stores/AiBuilderSessionProvider';
import { isAiBuilderUsageBlocked } from '../stores/aiBuilderUsagePolicy';
import { AiBuilderChatPanelHeader } from './AiBuilderChatPanelHeader';
import { AiBuilderComposer } from './AiBuilderComposer';
import { AiBuilderMessageList } from './AiBuilderMessageList';
import { AiBuilderUsageExhaustedCard } from './AiBuilderUsageExhaustedCard';
import { AiBuilderUsageExhaustedIntro } from './AiBuilderUsageExhaustedIntro';
import { AiBuilderWelcomeMessage } from './AiBuilderWelcomeMessage';

/** 채팅 패널 내용부 — 데스크톱 사이드 패널과 모바일 드로어 셸이 공유한다. */
export const AiBuilderChatPanelContent = ({
  shouldAutoFocusComposer = true
}: {
  shouldAutoFocusComposer?: boolean;
}) => {
  const {
    conversationEntryIntent,
    conversationQuestionState,
    messages
  } = useAiBuilderChatMessagesSelector();
  const {
    isGenerating,
    turnInProgress,
    usage
  } = useAiBuilderChatComposerSelector();
  const {
    closePanel,
    restartConversation
  } = useAiBuilderChatActions();
  const activeOperation = useAiBuilderActiveOperation();
  const sessionId = useAiBuilderChatSessionIdSafe();
  const editingQuizId = useAtomValue(editingQuizIdAtom);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const lastMessageId = messages.at(-1)?.id;
  const hasActiveAiWork = isGenerating || turnInProgress;
  const {
    closeConfirmDialog,
    handleClosePanel
  } = useAiBuilderCloseConfirmDialog({
    hasActiveAiWork,
    // 즉시 닫기/확인 다이얼로그 확정 닫기 모두 이 콜백으로 수렴한다 —
    // 실제로 닫히는 순간에만 1회 발화.
    onClose: () => {
      trackEvent('quiz_ai_builder_chat_close', {
        had_active_turn: hasActiveAiWork,
        message_count: messages.length,
        session_id: sessionId ?? ''
      });
      closePanel();
    }
  });
  useAiBuilderChatAutoScroll({
    contentRef,
    lastMessageId,
    messageCount: messages.length,
    scrollRef
  });

  // 파일 드롭 감지 범위는 패널 전체 — 오버레이 자체는 composer 입력창 바닥 기준으로
  // 렌더된다(AiBuilderComposerShell).
  const {
    isDraggingFile,
    fileDropZoneProps
  } = useAiBuilderComposerFileDrop();

  // 무료 대화 소진 상태: 대화 시작 전(메시지 없음)이면 초기 시스템 메시지는 그대로
  // 두고 그 위를 인트로 오버레이로 덮고, 대화 진행 중이면 composer 위에 업그레이드
  // 카드를 얹는다.
  const usageBlocked = isAiBuilderUsageBlocked(usage);
  const showUsageExhaustedIntro = usageBlocked && messages.length === 0;
  const showUsageExhaustedCard = usageBlocked && messages.length > 0;
  return <div className="flex min-h-0 flex-1 flex-col" data-ai-builder-chat-drop-zone {...fileDropZoneProps}
  // 편집 중 카드는 document mousedown outside-click으로 편집 모드를 닫는다
  // (EditableQuizCard). 패널 내 상호작용(action chip 선택 등)이 "카드 밖
  // 클릭"으로 오인되면 선택 문제 컨텍스트가 풀리므로 전파를 끊는다.
  onMouseDown={event => {
    if (editingQuizId) {
      event.stopPropagation();
    }
  }} data-sentry-component="AiBuilderChatPanelContent" data-sentry-source-file="AiBuilderChatPanelContent.tsx">
      <AiBuilderChatPanelHeader onClosePanel={() => {
      void handleClosePanel();
    }} onRestartConversation={restartConversation} restartDisabled={activeOperation === 'generator'} data-sentry-element="AiBuilderChatPanelHeader" data-sentry-source-file="AiBuilderChatPanelContent.tsx" />

      <div className="relative flex min-h-0 flex-1 flex-col justify-end">
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[30px] bg-gradient-to-b from-white to-[rgba(255,255,255,0)]" />
          <div ref={scrollRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-ai-builder-chat-scroll>
            <div ref={contentRef}
          // pt는 fade 높이(30px)와 동기 — 스크롤 최상단에서 첫 메시지가 fade에 가려지지 않게 한다.
          className="flex min-h-full min-w-0 flex-col justify-end gap-none pb-none pt-[30px]" data-ai-builder-chat-content>
              {messages.length > 0 ? <AiBuilderMessageList /> : <AiBuilderWelcomeMessage entryIntent={conversationEntryIntent ?? 'chat'} initialQuestionState={conversationQuestionState} />}
            </div>
          </div>
          {/* 초기 시스템 메시지 위를 덮는 소진 인트로 오버레이 — 메시지는 뒤에
              남고 backdrop-blur 로 흐려진다(composer 는 덮지 않는다). */}
          {showUsageExhaustedIntro && <div className="absolute inset-0 z-20 p-md">
              <AiBuilderUsageExhaustedIntro usageLimit={usage?.limit} />
            </div>}
        </div>
        <div className="flex flex-col gap-sm px-md pb-md">
          {showUsageExhaustedCard && <AiBuilderUsageExhaustedCard />}
          <AiBuilderComposer dropOverlayVisible={isDraggingFile} shouldAutoFocus={shouldAutoFocusComposer} data-sentry-element="AiBuilderComposer" data-sentry-source-file="AiBuilderChatPanelContent.tsx" />
        </div>
      </div>
      {closeConfirmDialog}
    </div>;
};